/**
 * Outputs / Review inbox — lists the tenant's generated deliverables (recorded
 * by agents into the server-side outputs/review tree) and downloads them
 * through the app. The download streams from the backend (`FileResponse`), so a
 * reviewer fetches artifacts inside the ZTD boundary — no filesystem access, no
 * external link — matching the GPS "human review before external sharing" rule.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileText, Inbox, Loader2, RefreshCw } from "lucide-react";
import { apiv2, type OutputArtifact } from "../apiv2";

const AGENT_LABEL: Record<string, string> = {
  "data-semantic": "Semantic Intelligence",
  "metadata-curator": "Metadata Catalog",
  "data-guardian": "Data Masking",
};

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function OutputsInboxView() {
  const [artifacts, setArtifacts] = useState<OutputArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setArtifacts(await apiv2.listOutputs());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load outputs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDownload = useCallback(async (a: OutputArtifact) => {
    setDownloading(a.artifact_id);
    try {
      await apiv2.downloadOutput(a.artifact_id, a.filename);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(null);
    }
  }, []);

  // Group by agent for a tidy review inbox.
  const grouped = useMemo(() => {
    const m = new Map<string, OutputArtifact[]>();
    for (const a of artifacts) {
      const k = a.agent;
      (m.get(k) ?? m.set(k, []).get(k)!).push(a);
    }
    return [...m.entries()];
  }, [artifacts]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="outputs-inbox">
      <div className="flex items-center gap-3 mb-1">
        <Inbox size={22} className="text-brand-500" />
        <h1 className="text-xl font-bold text-white">Outputs / Review</h1>
        <button
          data-testid="outputs-refresh"
          onClick={() => void load()}
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <p className="text-sm text-white/40 mb-5">
        AI-generated deliverables staged for human review. Download in-app — no
        files leave the ZTD boundary.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 text-sm py-12 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div
          data-testid="outputs-error"
          className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3"
        >
          {error}
        </div>
      ) : artifacts.length === 0 ? (
        <div
          data-testid="outputs-empty"
          className="text-center text-white/30 text-sm py-16 border border-dashed border-white/10 rounded-xl"
        >
          No outputs yet. Generate a model, catalog, or masking script and it
          will appear here for review.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([agent, items]) => (
            <div key={agent}>
              <h2 className="text-[11px] uppercase tracking-wider text-white/30 mb-2">
                {AGENT_LABEL[agent] ?? agent} ({items.length})
              </h2>
              <div className="space-y-1.5">
                {items.map((a) => (
                  <div
                    key={a.artifact_id}
                    data-testid="outputs-artifact"
                    data-artifact-id={a.artifact_id}
                    className="glass rounded-lg px-4 py-2.5 flex items-center gap-3"
                  >
                    <FileText size={15} className="text-brand-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white/80 truncate">
                        {a.label}
                      </p>
                      <p className="text-[11px] text-white/30 truncate">
                        {a.filename} · {a.kind} · {fmtSize(a.size)} ·{" "}
                        {new Date(a.created_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      data-testid="outputs-download"
                      onClick={() => void handleDownload(a)}
                      disabled={downloading === a.artifact_id}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white transition-colors"
                    >
                      {downloading === a.artifact_id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Download size={13} />
                      )}
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
